import { makeAutoObservable } from "mobx"
import { AxiosResponse } from "axios"

import {
  APIResponse,
  authApi,
  catchErrors,
  merchantApi,
  referrerApi,
} from "@src/shared/api"
import {
  InlineObject12,
  MerchantResponse,
  VerificationOperationCodeSendResponse,
} from "@src/shared/api/generated"
import { checkPartner } from "@src/shared/checkPartner"

// TODO: Remove this type and use the one from the generated API
export type Merchant = MerchantResponse & {
  status: "blocked" | "confirmed" | "pending" | "unconfirmed"
}

export type LoginReqType = {
  email: string
  otp?: string
  password: string
  re_captcha?: string
}

class AuthStore {
  confirmCodeData: VerificationOperationCodeSendResponse | null = null

  fetchKybUrl = async () => {
    this.isLoading = true

    return merchantApi
      .getMerchantKybUrl()
      .then(({ data: { data: { url } = {} } }) => {
        if (url) {
          this.kybLink = url
        }

        return url
      })
      .catch(catchErrors)
      .finally(() => {
        this.isLoading = false
      })
  }

  fetchMerchant = () =>
    merchantApi.getMerchantInfo().then<Merchant>((response) => {
      const {
        data: { data: merchant },
      } = response as AxiosResponse<APIResponse<Merchant>>

      if (merchant?.user_type === "referrer") {
        referrerApi.getReferrer().then((response) => {
          const {
            data: { data: referrer },
          } = response as AxiosResponse<APIResponse<Merchant>>

          return referrer as Merchant
        })
      }

      return merchant as Merchant
    })

  initialize = () => {
    this.isInitializing = true

    return this.fetchMerchant()
      .then((merchant) => {
        if (merchant) {
          this.merchant = merchant
        }

        return merchant
      })
      .catch(() => {})
      .finally(() => (this.isInitializing = false))
  }

  isInitializing: boolean = true

  isLoading = false

  isLogging: boolean = false

  kybLink: null | string = null

  login = (identity: LoginReqType) => {
    this.isLogging = true

    return authApi
      .login(identity)
      .then(this.fetchMerchant)
      .then((merchant) => {
        if (merchant) {
          this.merchant = merchant
        }

        return merchant
      })
      .finally(() => {
        this.isLogging = false
      })
  }

  logout = () => authApi.logout().then(() => (this.merchant = undefined))

  merchant?: Merchant

  reload = () => {
    return this.fetchMerchant().then((merchant) => (this.merchant = merchant))
  }

  reset = () => {
    this.merchant = undefined

    this.isInitializing = false
  }

  sendConfirmationCode = async () => {
    try {
      this.isLoading = true

      const { data } = await authApi.confirm()

      if (data) {
        // @ts-ignore
        this.confirmCodeData = data.data
      }
    } catch (error: any) {
      throw error
    } finally {
      this.isLoading = false
    }
  }

  constructor() {
    makeAutoObservable(this)
  }

  get hasActiveCode() {
    if (!this.confirmCodeData) {
      return false
    }

    const { expires_at } = this.confirmCodeData

    return new Date(expires_at) > new Date()
  }

  get isAnonymous(): boolean {
    return !this.merchant
  }

  get isConfirmedMerch() {
    const merch = this.merchant

    if (!merch) {
      return false
    }

    const subMerch = merch.submerchant

    if (subMerch) {
      return subMerch.status === "confirmed"
    }

    return merch.status === "confirmed"
  }

  get isMerchantEmailConfirmed(): boolean {
    const merch = this.merchant

    if (!merch) {
      return false
    }

    const subMerch = merch.submerchant

    if (subMerch) {
      return subMerch.status === "confirmed"
    }

    return merch.is_confirmed ?? false
  }

  get isOtpEnabled() {
    if (!this.merchantInfo) {
      return false
    }

    if (this.isSubMerch) {
      return (this.merchantInfo.submerchant as any).is_otp_enabled
    }

    return this.merchantInfo.is_otp_enabled
  }

  get isSendCryptoReferral(): boolean {
    return this.merchantInfo
      ? checkPartner(this.merchantInfo.referral_link_id)
      : false
  }

  get isSubMerch() {
    return Boolean(this.merchant?.submerchant)
  }

  get merchantInfo(): Merchant | undefined {
    return this.merchant
  }
}

export const authStore = new AuthStore()
